import { Head, Heading } from "zudoku/components";
import type { Reference } from "./define";

/*
  Head of a reference page: the title, the lead paragraph, and the page
  metadata, all from `sections.ts`. Writers add the <Method> blocks below it.

  <SdkPage />                  the overview page at the base path
  <SdkPage group="cart" />     the page of one group

  A group page opens with the description of the group, so a writer adds only
  the <Method> blocks. The overview page writes its own intro below the title.

  `createPage` binds this to one reference; the bound component is registered
  in zudoku.config.tsx and used by that reference's pages.
*/

export const createPage = (reference: Reference) => {
  const Page = ({ group: slug }: { group?: string }) => {
    const group = slug ? reference.find(slug) : undefined;
    if (slug && !group) {
      throw new Error(`${reference.path}: no group named "${slug}"`);
    }
    const label = group?.label ?? reference.title;
    const description = group?.description ?? reference.description;

    return (
      <>
        {/* Zudoku adds the canonical link itself once `canonicalUrlOrigin` is set. */}
        <Head>
          <title>
            {group ? `${label} · ${reference.title}` : reference.title}
          </title>
          <meta name="description" content={description} />
        </Head>
        {/* No `id`: nothing links to the title, and a group slug equals the
            slug of a method often enough that the two would collide on one
            page. Zudoku's Heading makes the anchor only when it gets an id. */}
        <Heading level={1}>{label}</Heading>
        {group && <p>{description}</p>}
      </>
    );
  };
  Page.displayName = `Page(${reference.path})`;
  return Page;
};
